import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo} from 'react'

import {ThreadHeader} from './ThreadHeader'
import type {ThreadPreviewComp as ThreadPreviewType} from '../../page-data/payloads/thread-previews'
import {PreviewAuthors} from './PreviewAuthors'
import {useGetThread, useIsSingleFileMode, useMarkerAndDiffNavigationActions} from '../../stores/selectors'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {extractCommentAuthors} from './PendingCommentPreview'

function panelThreadCollapsedStateStorageKey(id: string) {
  return `panel-thread-collapsed-state-${id}`
}

type ThreadPreviewProps = {
  onNavigateToDiffComment: () => void
  tabSize?: number
  threadPreview: ThreadPreviewType
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreview({
  onNavigateToDiffComment,
  tabSize,
  threadPreview,
  headingLevel,
}: ThreadPreviewProps) {
  const {previewInfo} = threadPreview
  const {threadId, firstComment} = previewInfo
  const commentId = firstComment?.id
  const filePath = previewInfo.path
  const isSingleFileMode = useIsSingleFileMode()
  const {navigateToSelectedComment} = useMarkerAndDiffNavigationActions()

  const getThread = useGetThread()

  const isRightOrLeftSide = previewInfo.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'

  const lineKey = previewInfo.subject?.originalEndLine
    ? (`${isRightOrLeftSide}${previewInfo.subject.originalEndLine}` as LineKey)
    : fileLevelCommentKey

  const thread = getThread(Number(previewInfo.threadId), previewInfo.path, lineKey)
  const isResolved = thread?.isResolved || false
  const isOutdated = previewInfo.isOutdated || false

  const navigateToComment = () => {
    if (isOutdated && previewInfo.originalDiffPathUri) {
      // eslint-disable-next-line no-restricted-syntax
      const url = new URL(previewInfo.originalDiffPathUri)
      url.searchParams.set('new_files_changed', 'true')
      window.location.href = url.toString()
    } else {
      window.location.hash = `#r${firstComment?.databaseId}`
      if (isSingleFileMode && firstComment?.databaseId) {
        navigateToSelectedComment(firstComment?.databaseId)
      }
    }
    onNavigateToDiffComment()
  }

  const [isCollapsed, setIsCollapsed] = useLocalStorage(panelThreadCollapsedStateStorageKey(threadId), false)

  const replyCount = previewInfo.threadPreviewComments.length
  const repliesText = replyCount === 0 ? 'No replies' : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`
  // The StaticUnifiedDiffPreview only looks at the subject, should probably by updated to only require that but for now
  // clean the comments so we don't have to match the typing by requesting a ton more data.
  const threadDiffPreview: typeof thread & {commentsData: {comments: Comment[]}} = {
    ...thread,
    id: thread?.id ?? '',
    commentsData: {comments: []},
  }

  if (!firstComment || !threadDiffPreview) return null

  const authors = extractCommentAuthors(previewInfo.commentId, thread?.commentsData ?? {comments: []})

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ThreadHeader
        firstCommentId={firstComment?.databaseId}
        isCollapsed={isCollapsed}
        threadPreview={threadPreview}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
        headingLevel={headingLevel}
        isResolved={isResolved}
        isOutdated={isOutdated}
        lineKey={lineKey}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={previewInfo?.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          <div>
            <ReviewThreadComment
              lineMarkersKey={lineKey}
              key={commentId}
              hideActions
              isInlineComment={false}
              comment={firstComment}
              filePath={filePath}
              index={0}
              isAnchorable={false}
              isOutdated={isOutdated}
              isThreadResolved={isResolved}
              threadId={threadId}
              originalDiffPathUri={previewInfo.originalDiffPathUri}
              headingLevel="h3"
            />
          </div>
          <div className="mb-2 px-2">
            <Button
              aria-label={`View ${repliesText}`}
              size="small"
              trailingVisual={ChevronRightIcon}
              variant="invisible"
              onClick={navigateToComment}
            >
              <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                <span>{repliesText}</span>
                <PreviewAuthors commentAuthors={authors} />
              </div>
            </Button>
          </div>
        </>
      )}
    </div>
  )
})

try{ ThreadPreview.displayName ||= 'ThreadPreview' } catch {}